import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listAllMenu,
  upsertMenuItem,
  deleteMenuItem,
  type MenuItemRow,
} from "@/lib/menu.functions";
import { MENU } from "@/data/menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AlertCircle, CheckCircle2, Eye, EyeOff, ImageIcon, Pencil, Plus, Search, Trash2, X,
} from "lucide-react";
import { ImageUpload } from "@/components/admin/ImageUpload";

export const Route = createFileRoute("/_authenticated/admin/menu")({
  head: () => ({ meta: [{ title: "Menu & Cocktails — Admin | Plunkets" }, { name: "robots", content: "noindex" }] }),
  component: MenuAdmin,
});

type FormState = {
  id?: string;
  slug: string;
  name: string;
  category: string;
  price: string;
  tagline: string;
  description: string;
  ingredients: string;
  pairings: string;
  image_url: string;
  badge: string;
  published: boolean;
  sort_order: number;
};

const empty: FormState = {
  slug: "", name: "", category: "Signature Cocktails", price: "€12",
  tagline: "", description: "", ingredients: "", pairings: "",
  image_url: "", badge: "", published: true, sort_order: 0,
};

const CATEGORY_OPTIONS = [
  "Signature Cocktails",
  "Classic Cocktails",
  "Beers on Tap",
  "From the Kitchen",
  "Sharing Boards",
] as const;

const FormSchema = z.object({
  slug: z.string().trim().min(2).max(120).regex(/^[a-z0-9-]+$/, "lowercase letters, numbers and dashes only"),
  name: z.string().trim().min(2).max(160),
  category: z.string().trim().min(2).max(80),
  price: z.string().trim().min(1).max(20),
  tagline: z.string().max(240).optional(),
  description: z.string().max(2000).optional(),
  image_url: z
    .string()
    .max(2000)
    .optional()
    .refine(
      (v) => !v || /^https?:\/\//i.test(v) || v.startsWith("/api/public/media/"),
      "Upload an image or paste a URL.",
    ),
  badge: z.string().max(40).optional(),
  published: z.boolean(),
  sort_order: z.number().int().min(0).max(9999),
});

type FieldErrors = Partial<Record<keyof FormState, string>>;

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function parseList(raw: string): string[] {
  return raw.split(/\r?\n|,\s*/).map((s) => s.trim()).filter(Boolean);
}

type Row = MenuItemRow & { source: "db" | "seed" };

function MenuAdmin() {
  const list = useServerFn(listAllMenu);
  const upsert = useServerFn(upsertMenuItem);
  const del = useServerFn(deleteMenuItem);
  const qc = useQueryClient();

  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [toDelete, setToDelete] = useState<Row | null>(null);
  const [filter, setFilter] = useState<string>("All");
  const [search, setSearch] = useState("");

  const { data: dbItems = [], isLoading } = useQuery({
    queryKey: ["admin-menu"],
    queryFn: () => list(),
  });

  const dbSlugs = useMemo(() => new Set(dbItems.map((i) => i.slug)), [dbItems]);
  const seedOnly = MENU.filter((m) => !dbSlugs.has(m.slug));

  const allDisplay: Row[] = [
    ...dbItems.map<Row>((i) => ({ ...i, source: "db" })),
    ...seedOnly.map<Row>((m) => ({
      id: `seed:${m.slug}`,
      slug: m.slug,
      name: m.name,
      category: m.category,
      price: m.price,
      tagline: m.tagline,
      description: m.description,
      ingredients: m.ingredients,
      pairings: m.pairings ?? [],
      image_url: null,
      badge: m.badge ?? null,
      published: true,
      sort_order: 0,
      created_at: "",
      updated_at: "",
      source: "seed",
    })),
  ];

  const categories = ["All", ...Array.from(new Set([...CATEGORY_OPTIONS, ...allDisplay.map((i) => i.category)]))];
  const filtered = allDisplay
    .filter((i) => filter === "All" || i.category === filter)
    .filter((i) =>
      !search.trim() ||
      i.name.toLowerCase().includes(search.toLowerCase()) ||
      i.slug.toLowerCase().includes(search.toLowerCase()),
    );

  const counts = useMemo(() => ({
    total: allDisplay.length,
    hidden: dbItems.filter((i) => !i.published).length,
  }), [allDisplay.length, dbItems]);

  const save = useMutation({
    mutationFn: async () => {
      const parsed = FormSchema.safeParse(form);
      if (!parsed.success) {
        const errs: FieldErrors = {};
        for (const issue of parsed.error.issues) {
          const k = issue.path[0] as keyof FormState;
          if (k && !errs[k]) errs[k] = issue.message;
        }
        setFieldErrors(errs);
        const firstKey = Object.keys(errs)[0];
        const labels: Record<string, string> = {
          name: "Name", slug: "Slug", category: "Category", price: "Price",
          tagline: "Tagline", description: "Description", image_url: "Photo",
          badge: "Badge", sort_order: "Sort order",
        };
        const missing = Object.entries(errs).map(([k, v]) => `${labels[k] ?? k}: ${v}`).join(" · ");
        if (typeof document !== "undefined" && firstKey) {
          const el = document.querySelector(`[data-field="${firstKey}"]`) as HTMLElement | null;
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
          (el?.querySelector("input, textarea, select") as HTMLElement | null)?.focus();
        }
        throw new Error(missing || "Please fix the highlighted fields.");
      }
      setFieldErrors({});
      return upsert({
        data: {
          id: form.id,
          slug: parsed.data.slug,
          name: parsed.data.name,
          category: parsed.data.category,
          price: parsed.data.price,
          tagline: parsed.data.tagline ?? "",
          description: parsed.data.description ?? "",
          ingredients: parseList(form.ingredients),
          pairings: parseList(form.pairings),
          image_url: parsed.data.image_url || null,
          badge: parsed.data.badge || null,
          published: parsed.data.published,
          sort_order: parsed.data.sort_order,
        },
      });
    },
    onSuccess: () => {
      const wasEdit = !!form.id;
      setForm(empty);
      setError(null);
      setFieldErrors({});
      setSuccess(wasEdit ? "Item updated — live on the site." : "Item added — live on the site.");
      qc.invalidateQueries({ queryKey: ["admin-menu"] });
      qc.invalidateQueries({ queryKey: ["public-menu"] });
      setTimeout(() => setSuccess(null), 4000);
    },
    onError: (e: Error) => { setError(e.message); setSuccess(null); },
  });

  const remove = useMutation({
    mutationFn: async (row: Row) => {
      if (row.source === "db") return del({ data: { id: row.id } });
      // Seed items: create a hidden DB row so merge removes it from public site.
      return upsert({
        data: {
          slug: row.slug,
          name: row.name,
          category: row.category,
          price: row.price,
          tagline: row.tagline,
          description: row.description,
          ingredients: row.ingredients ?? [],
          pairings: row.pairings ?? [],
          image_url: null,
          badge: row.badge,
          published: false,
          sort_order: row.sort_order ?? 0,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-menu"] });
      qc.invalidateQueries({ queryKey: ["public-menu"] });
      setToDelete(null);
      setSuccess("Item hidden from the public site.");
      setTimeout(() => setSuccess(null), 3000);
    },
  });

  const edit = (i: Row) => {
    setFieldErrors({});
    setError(null);
    setSuccess(null);
    setForm({
      id: i.source === "seed" ? undefined : i.id,
      slug: i.slug,
      name: i.name,
      category: i.category,
      price: i.price,
      tagline: i.tagline,
      description: i.description,
      ingredients: (i.ingredients ?? []).join("\n"),
      pairings: (i.pairings ?? []).join("\n"),
      image_url: i.image_url ?? "",
      badge: i.badge ?? "",
      published: i.published,
      sort_order: i.sort_order ?? 0,
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (!form.id && form.name && !form.slug) {
      setForm((f) => ({ ...f, slug: slugify(f.name) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name]);

  const errCls = "mt-1 flex items-center gap-1 text-xs text-destructive";

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Menu & Cocktails</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage every item shown on the Cocktails & Menu page, single product pages and the homepage highlights.
            Changes go live immediately.
          </p>
        </div>
        <div className="flex gap-6 text-xs uppercase tracking-widest text-muted-foreground">
          <div><span className="block text-lg font-semibold text-foreground">{counts.total}</span>Total items</div>
          <div><span className="block text-lg font-semibold text-foreground">{counts.hidden}</span>Hidden</div>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          save.mutate(undefined, { onSettled: () => setBusy(false) });
        }}
        className="rounded-2xl border border-border bg-card/40"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <p className="text-sm font-semibold">
            {form.id ? "Editing item" : "New item"}
          </p>
          {(form.id || form.name) && (
            <button
              type="button"
              onClick={() => { setForm(empty); setFieldErrors({}); setError(null); }}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>

        <div className="grid gap-5 p-5 md:grid-cols-2">
          {/* Section: Basics */}
          <div className="md:col-span-2">
            <p className="mb-3 text-[11px] uppercase tracking-widest text-muted-foreground">Basics</p>
            <div className="grid gap-4 md:grid-cols-2">
              <div data-field="name">
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => { setForm({ ...form, name: e.target.value }); if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: undefined }); setError(null); }}
                  placeholder="Pornstar Martini"
                  aria-invalid={!!fieldErrors.name}
                />
                {fieldErrors.name && <p className={errCls}><AlertCircle size={12} />{fieldErrors.name}</p>}
              </div>
              <div data-field="slug">
                <Label>Slug *</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => { setForm({ ...form, slug: e.target.value }); if (fieldErrors.slug) setFieldErrors({ ...fieldErrors, slug: undefined }); setError(null); }}
                  placeholder="pornstar-martini"
                  aria-invalid={!!fieldErrors.slug}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">URL: /menu/{form.slug || "your-slug"}</p>
                {fieldErrors.slug && <p className={errCls}><AlertCircle size={12} />{fieldErrors.slug}</p>}
              </div>
              <div data-field="category">
                <Label>Category *</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {Array.from(new Set([...CATEGORY_OPTIONS, form.category].filter(Boolean))).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div data-field="price">
                <Label>Price *</Label>
                <Input
                  value={form.price}
                  onChange={(e) => { setForm({ ...form, price: e.target.value }); if (fieldErrors.price) setFieldErrors({ ...fieldErrors, price: undefined }); setError(null); }}
                  placeholder="€12"
                  aria-invalid={!!fieldErrors.price}
                />
                {fieldErrors.price && <p className={errCls}><AlertCircle size={12} />{fieldErrors.price}</p>}
              </div>
            </div>
          </div>

          {/* Section: Photo */}
          <div className="md:col-span-2" data-field="image_url">
            <p className="mb-3 text-[11px] uppercase tracking-widest text-muted-foreground">Photo</p>
            <ImageUpload
              value={form.image_url}
              onChange={(v) => setForm({ ...form, image_url: v })}
              folder="menu"
              aspect="square"
              hint="Square 1024×1024 recommended. Shown on menu cards and the product page."
            />
            {fieldErrors.image_url && <p className={errCls}><AlertCircle size={12} />{fieldErrors.image_url}</p>}
          </div>

          {/* Section: Copy */}
          <div className="md:col-span-2">
            <p className="mb-3 text-[11px] uppercase tracking-widest text-muted-foreground">Description</p>
            <div className="grid gap-4">
              <div>
                <Label>Tagline</Label>
                <Input
                  value={form.tagline}
                  onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  placeholder="Passion fruit, vanilla vodka, prosecco chaser."
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Full story of the drink or dish. Shown on the single product page."
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Ingredients</Label>
                  <Textarea
                    rows={4}
                    value={form.ingredients}
                    onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
                    placeholder={"Vanilla vodka\nPassoã\nFresh passion fruit"}
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">One per line (or comma-separated).</p>
                </div>
                <div>
                  <Label>Pairings</Label>
                  <Textarea
                    rows={4}
                    value={form.pairings}
                    onChange={(e) => setForm({ ...form, pairings: e.target.value })}
                    placeholder={"Espresso Martini\nLoaded Fries"}
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">Suggested items to pair with this one.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Display */}
          <div className="md:col-span-2">
            <p className="mb-3 text-[11px] uppercase tracking-widest text-muted-foreground">Display</p>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Badge</Label>
                <Input
                  value={form.badge}
                  onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  placeholder="Most Loved · Bartender's Pick"
                />
              </div>
              <div>
                <Label>Sort order</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">Lower = shown first.</p>
              </div>
              <div className="flex items-end">
                <label className="mt-1 inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm({ ...form, published: e.target.checked })}
                    className="h-4 w-4"
                  />
                  Visible on the public menu
                </label>
              </div>
            </div>
          </div>

          {error && (
            <p className="md:col-span-2 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
              <AlertCircle size={14} /> {error}
            </p>
          )}
          {success && (
            <p className="md:col-span-2 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 p-2 text-sm text-primary">
              <CheckCircle2 size={14} /> {success}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-border bg-card/80 px-5 py-3 backdrop-blur">
          {(form.id || form.name) && (
            <Button type="button" variant="ghost" onClick={() => { setForm(empty); setFieldErrors({}); setError(null); }}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={busy}>
            <Plus size={14} className="mr-1" /> {busy ? "Saving…" : form.id ? "Update item" : "Add item"}
          </Button>
        </div>
      </form>

      <div className="rounded-2xl border border-border bg-card/30">
        <div className="flex flex-col gap-3 border-b border-border px-5 py-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="text-sm font-semibold">All menu items</div>
            <span className="text-xs text-muted-foreground">{filtered.length} shown</span>
          </div>
          <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center md:justify-end">
            <div className="relative w-full max-w-xs">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or slug…"
                className="h-9 pl-7"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilter(c)}
                  className={`rounded-full border px-3 py-1 text-xs transition ${
                    filter === c
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No items match your filters.</div>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((i) => (
              <li key={i.id} className="flex items-start justify-between gap-4 px-5 py-4">
                <div className="flex min-w-0 gap-3">
                  {i.image_url ? (
                    <img src={i.image_url} alt="" className="h-14 w-14 shrink-0 rounded-md object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-muted/40 text-muted-foreground">
                      <ImageIcon size={18} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wider text-primary">
                      {i.published ? <Eye size={12} /> : <EyeOff size={12} className="text-muted-foreground" />}
                      {i.category}
                      {i.source === "seed" && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-muted-foreground">
                          Built-in
                        </span>
                      )}
                      {!i.published && (
                        <span className="rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-destructive">
                          Hidden
                        </span>
                      )}
                    </div>
                    <div className="mt-1 font-display text-lg">{i.name}</div>
                    <div className="text-xs text-muted-foreground">{i.price} · /menu/{i.slug}</div>
                    {i.tagline && <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{i.tagline}</div>}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="ghost" onClick={() => edit(i)} title="Edit">
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setToDelete(i)} title={i.source === "db" ? "Delete" : "Hide from site"}>
                    <Trash2 size={14} className="text-destructive" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {toDelete?.source === "seed" ? `Hide "${toDelete?.name}"?` : `Delete "${toDelete?.name}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.source === "seed"
                ? "This built-in item will be hidden from the public Cocktails & Menu page. You can bring it back by editing and toggling visibility on."
                : "This removes the item from the public site permanently."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => toDelete && remove.mutate(toDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {toDelete?.source === "seed" ? "Hide" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
